import _styled, { default as _styled2, default as _styled3, default as _styled4, default as _styled5, default as _styled6, default as _styled7, default as _styled8, default as _styled9, default as _styled10, default as _styled11, default as _styled12, default as _styled13 } from "styled-components";
import React from "react";
import { useForm } from "react-hook-form";
import type { SanityCustomerIoEventMetadataPair, SanityPageBuilderCustomerIoFormField } from "@ouster/shared";
import { useFormState } from "~/features/ui/form/use-form-state";
import { Wrap } from "~/features/ui/wrap";
import { getThemeStyle, type Mode, type Theme } from "~/features/ui/get-theme-style";
import { takeFirst, scrollElementIntoView } from "~/features/app/utils";
import { Input } from "~/features/ui/form/input";
import { TextArea } from "~/features/ui/form/textarea";
import { Select } from "~/features/ui/form/select";
import { Checkbox } from "~/features/ui/form/checkbox";
import { useLocale } from "~/features/i18n/use-locale";
import { FieldError } from "~/features/ui/form/field-error";
import { validateRecaptcha, preloadRecaptcha } from "~/features/re-captcha/utils";
import { Button } from "~/features/ui/button";
import ArrowRight from "~/public/icons/arrow-right.svg";
import Loading from "~/public/icons/loading.svg";
const translations = {
  submit: {
    en: "Submit",
    zh: "提交",
    ja: "送信",
    ko: "제출"
  },
  privacy: {
    en: "I agree to allow Ouster to store and process my personal data.",
    zh: "我同意Ouster存储和处理我的个人数据。",
    ja: "Ousterに個人データの保存と処理を許可します。",
    ko: "Ouster가 내 개인 데이터를 저장하고 처리하는 데 동의합니다."
  }
} as const;
const variants = {
  default: {
    "paddingTop": "4rem",
    "paddingBottom": "4rem",
    "@media (min-width: 1024px)": {
      "paddingLeft": "12rem",
      "paddingRight": "12rem",
      "paddingTop": "8rem",
      "paddingBottom": "8rem"
    }
  },
  condensed: {
    "paddingTop": "4rem",
    "paddingBottom": "4rem",
    "@media (min-width: 1024px)": {
      "paddingLeft": "4rem",
      "paddingRight": "4rem"
    }
  }
} as const;
type SectionDataAttrs = Record<`data-${string}`, string | undefined>;
export type CustomerIoFormBlockViewProps = {
  anchor?: Array<{
    id?: string | null;
    name?: string | null;
  }> | {
    id?: string | null;
    name?: string | null;
  } | null;
  description?: string | null;
  disclaimer?: React.ReactNode;
  eventMetadata?: SanityCustomerIoEventMetadataPair[] | null;
  eventName?: string | null;
  fields?: SanityPageBuilderCustomerIoFormField[] | null;
  formName?: string | null;
  hasTopDivider?: boolean | null;
  headline?: string | null;
  layout?: string | null;
  mode?: string | null;
  privacyCheckboxLabel?: string | null;
  sectionProps?: React.ComponentPropsWithoutRef<"section"> & SectionDataAttrs;
  submitLabel?: string | null;
  successMessage?: string | null;
  successMessageImage?: React.ReactNode;
  successMessageText?: React.ReactNode;
  theme?: string | null;
};
export function CustomerIoFormBlockView({
  anchor,
  description,
  disclaimer,
  eventMetadata,
  eventName,
  fields = [],
  formName,
  hasTopDivider = true,
  headline,
  layout = "default",
  mode = "light",
  privacyCheckboxLabel,
  sectionProps,
  submitLabel,
  successMessage,
  successMessageImage,
  successMessageText,
  theme = "white"
}: CustomerIoFormBlockViewProps) {
  const locale = useLocale();
  const layoutKey = layout === "condensed" ? "condensed" : "default";
  const themeStyle = getThemeStyle({
    mode: mode as Mode,
    theme: theme as Theme
  });
  // getThemeStyle maps the "white" theme to grey-20 (#efeff6); render it as true
  // white here (matching textMedia1_2) so a white-themed form is #ffffff, not lavender.
  const background = theme === "grey" ? themeStyle.background : {
    "--tw-bg-opacity": "1",
    "backgroundColor": "rgb(255 255 255 / var(--tw-bg-opacity))"
  };
  const [status, setStatus] = useFormState();
  const containerRef = React.useRef<HTMLDivElement>(null);
  const {
    handleSubmit,
    register,
    formState: {
      errors
    }
  } = useForm();
  const renderableFields = (fields ?? []).filter((field): field is SanityPageBuilderCustomerIoFormField => Boolean(field?.key));
  const onSubmit = handleSubmit(async data => {
    // The consent checkbox gates submission; the server drops it too.
    delete data.privacy;
    setStatus({
      state: "LOADING"
    });
    const isRecaptchaValid = await validateRecaptcha();
    if (!isRecaptchaValid) {
      setStatus({
        state: "ERROR",
        message: "There was an issue with reCAPTCHA, please try again later."
      });
      return;
    }

    // Code-injected event context: page location + locale + any utm_* params.
    const context: Record<string, string> = {
      url: window.location.href,
      path: window.location.pathname,
      locale
    };
    for (const [param, value] of new URLSearchParams(window.location.search)) {
      if (param.startsWith("utm_")) {
        context[param] = value;
      }
    }

    // Per-field destination overrides; the server defaults absent keys to "both".
    const fieldConfig: Record<string, string> = {};
    for (const field of renderableFields) {
      if (field.key && field.destination && field.destination !== "both") {
        fieldConfig[field.key] = field.destination;
      }
    }

    // Flatten the author-set metadata pairs into a record for the event data.
    const metadata: Record<string, string> = {};
    for (const pair of eventMetadata ?? []) {
      if (pair?.key) {
        metadata[pair.key] = pair.value ?? "";
      }
    }
    try {
      const res = await fetch("/api/submit-customerio-form", {
        method: "POST",
        body: JSON.stringify({
          fields: data,
          fieldConfig,
          formName,
          eventName: eventName || undefined,
          eventMetadata: metadata,
          context
        })
      });
      if (res.status !== 200) {
        const payload = (await res.json().catch(() => null)) as {
          message?: string;
        } | null;
        throw new Error(payload?.message || "Error");
      }
      setStatus({
        state: "SUCCESS"
      });
      if (containerRef.current) {
        scrollElementIntoView(containerRef, 80);
      }
    } catch (e: any) {
      console.error(e);
      setStatus({
        state: "ERROR",
        message: e.message
      });
    }
  });
  const isLoading = status.state === "LOADING";
  return <_StyledSection {...sectionProps} id={takeFirst(anchor)?.id || undefined} data-anchor-name={takeFirst(anchor)?.name || undefined} $_css={[hasTopDivider && {
    "borderTopWidth": "1px"
  }, themeStyle.text, themeStyle.border, background]}>
      <Wrap mode={mode as Mode}>
        <_StyledDiv>
          <_StyledDiv2 ref={containerRef} $_css2={[variants[layoutKey], themeStyle.border]}>
            {status.state === "SUCCESS" ? <_StyledDiv3>
                <_StyledDiv4>
                  <_StyledH>{successMessage}</_StyledH>
                  {successMessageText}
                </_StyledDiv4>
                {successMessageImage}
              </_StyledDiv3> : <form onFocusCapture={() => preloadRecaptcha()} data-customerio-form-name={formName || undefined}>
                {(headline || description) && <_StyledDiv5>
                    {headline && <_StyledH2>{headline}</_StyledH2>}
                    {description && <_StyledP>{description}</_StyledP>}
                  </_StyledDiv5>}
                <_StyledDiv6>
                  {renderableFields.map(field => {
                const key = field.key as string;
                const label = field.label || key;
                const rules = field.required ? {
                  required: {
                    value: true,
                    message: "This field is required"
                  }
                } : {};
                const error = errors[key]?.message as string | undefined;
                const common = {
                  label,
                  error,
                  required: Boolean(field.required),
                  disabled: isLoading,
                  placeholder: field.placeholder || undefined
                };
                if (field.type === "textarea") {
                  return <TextArea key={key} {...common} register={register(key, rules)} />;
                }
                if (field.type === "select") {
                  const options = (field.options ?? []).map(option => ({
                    label: option,
                    value: option
                  }));
                  return <Select key={key} {...common} options={options} register={register(key, rules)} />;
                }
                return <Input key={key} type={field.type === "email" ? "email" : field.type === "tel" ? "tel" : "text"} {...common} register={register(key, rules)} />;
              })}
                </_StyledDiv6>
                <_StyledDiv7>
                  {disclaimer}
                  <_StyledDiv8>
                    <Checkbox name="privacy" label={privacyCheckboxLabel || translations.privacy[locale]} register={register("privacy", {
                  required: {
                    value: true,
                    message: "Consent is required"
                  }
                })} error={errors["privacy"]?.message as string} />
                    <_StyledDiv9>
                      <Button onClick={onSubmit} variant="secondary" disabled={isLoading} icon={isLoading ? Loading : ArrowRight}>
                        {submitLabel || translations.submit[locale]}
                      </Button>
                      {status.state === "ERROR" && status.message && <FieldError message={status.message} />}
                    </_StyledDiv9>
                  </_StyledDiv8>
                </_StyledDiv7>
              </form>}
          </_StyledDiv2>
        </_StyledDiv>
      </Wrap>
    </_StyledSection>;
}
var _StyledSection = /*#__PURE__*/_styled("section").withConfig({
  componentId: "sc-sf-8bf7yc-0"
})`${p => p.$_css}`;
var _StyledDiv = _styled2("div").withConfig({
  componentId: "sc-sf-8bf7yc-1"
})({
  "position": "relative",
  "zIndex": "10",
  "paddingLeft": "4rem",
  "paddingRight": "4rem",
  "@media (min-width: 1024px)": {
    "paddingLeft": "0",
    "paddingRight": "0"
  }
});
var _StyledDiv2 = _styled3("div").withConfig({
  componentId: "sc-sf-8bf7yc-2"
})`${p => p.$_css2}`;
var _StyledDiv3 = _styled4("div").withConfig({
  componentId: "sc-sf-8bf7yc-3"
})({
  "display": "flex",
  "flexDirection": "column",
  "rowGap": "4rem"
});
var _StyledDiv4 = _styled5("div").withConfig({
  componentId: "sc-sf-8bf7yc-4"
})({
  "display": "flex",
  "flexDirection": "column",
  "rowGap": "2.4rem"
});
var _StyledH = _styled6("h2").withConfig({
  componentId: "sc-sf-8bf7yc-5"
})({
  "fontSize": "2.2rem",
  "lineHeight": "1.2",
  "fontWeight": "400",
  "@media (min-width: 1024px)": {
    "fontSize": "3.2rem",
    "lineHeight": "1.2",
    "letterSpacing": "0.032rem",
    "fontWeight": "400"
  }
});
var _StyledDiv5 = _styled7("div").withConfig({
  componentId: "sc-sf-8bf7yc-6"
})({
  "marginBottom": "4rem",
  "display": "flex",
  "flexDirection": "column",
  "rowGap": "1.6rem"
});
var _StyledH2 = _styled8("h2").withConfig({
  componentId: "sc-sf-8bf7yc-7"
})({
  "fontSize": "2.2rem",
  "lineHeight": "1.2",
  "fontWeight": "400",
  "@media (min-width: 1024px)": {
    "fontSize": "3.2rem",
    "lineHeight": "1.2",
    "letterSpacing": "0.032rem",
    "fontWeight": "400"
  }
});
var _StyledP = _styled9("p").withConfig({
  componentId: "sc-sf-8bf7yc-8"
})({
  "--tw-text-opacity": "1",
  "color": "rgb(92 92 92 / var(--tw-text-opacity))"
});
var _StyledDiv6 = _styled10("div").withConfig({
  componentId: "sc-sf-8bf7yc-9"
})({
  "position": "relative",
  "zIndex": "10",
  "display": "flex",
  "flexDirection": "column",
  "gap": "1.6rem"
});
var _StyledDiv7 = _styled11("div").withConfig({
  componentId: "sc-sf-8bf7yc-10"
})({
  "marginTop": "4rem",
  "display": "flex",
  "flexDirection": "column",
  "gap": "4rem"
});
var _StyledDiv8 = _styled12("div").withConfig({
  componentId: "sc-sf-8bf7yc-11"
})({
  "display": "flex",
  "flexDirection": "column",
  "alignItems": "flex-start",
  "gap": "4rem"
});
var _StyledDiv9 = /*#__PURE__*/_styled13("div").withConfig({
  displayName: "customer-io-form___StyledDiv9",
  componentId: "sc-1o48mou-0"
})({
  "position": "relative"
});